import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api';

interface ActiveVisit {
    visitId: number;
    path: string;
}

const HEARTBEAT_INTERVAL_MS = 30000;
const ACTIVE_IDLE_TIMEOUT_MS = 2 * 60 * 1000;

function pageLabelForPath(path: string): string {
    if (path === '/') return 'Home';
    return path.split('?')[0].split('/').filter(Boolean).join(' / ') || path;
}

export function usePageUsageAnalytics(enabled: boolean) {
    const location = useLocation();
    const activeVisitRef = useRef<ActiveVisit | null>(null);
    const sequenceRef = useRef(0);
    const collectionEnabledRef = useRef<boolean | null>(null);
    const lastActivityAtRef = useRef<number>(Date.now());
    const lastActiveSentAtRef = useRef<number>(Date.now());
    const currentPath = `${location.pathname}${location.search || ''}`;
    const trackingEnabled = enabled && (typeof navigator === 'undefined' || navigator.webdriver !== true);

    useEffect(() => {
        if (!trackingEnabled) return;
        const recordActivity = () => {
            const now = Date.now();
            lastActivityAtRef.current = now;
            if (lastActiveSentAtRef.current <= 0) {
                lastActiveSentAtRef.current = now;
            }
        };
        const events: Array<keyof WindowEventMap> = ['click', 'keydown', 'scroll', 'wheel', 'pointerdown', 'pointermove', 'touchstart'];
        events.forEach((eventName) => window.addEventListener(eventName, recordActivity, { passive: true }));
        return () => {
            events.forEach((eventName) => window.removeEventListener(eventName, recordActivity));
        };
    }, [trackingEnabled]);

    useEffect(() => {
        if (!trackingEnabled) return;
        let cancelled = false;
        const sequence = sequenceRef.current + 1;
        sequenceRef.current = sequence;

        const startVisit = () => {
            if (cancelled || collectionEnabledRef.current === false) return;

            const previousVisit = activeVisitRef.current;
            activeVisitRef.current = null;
            if (previousVisit) {
                api.system.updateUsageVisit(previousVisit.visitId, { ended: true, exit_reason: 'route_change' }).catch(() => undefined);
            }

            lastActivityAtRef.current = Date.now();
            lastActiveSentAtRef.current = Date.now();
            api.system.startUsageVisit({
                path: currentPath,
                label: pageLabelForPath(currentPath),
                source: 'route',
            }).then((visit) => {
                if (!visit) {
                    collectionEnabledRef.current = false;
                    return;
                }
                if (cancelled || sequence !== sequenceRef.current) {
                    api.system.updateUsageVisit(visit.visit_id, { ended: true, exit_reason: 'superseded' }).catch(() => undefined);
                    return;
                }
                activeVisitRef.current = {
                    visitId: visit.visit_id,
                    path: currentPath,
                };
            }).catch(() => undefined);
        };

        if (collectionEnabledRef.current === null) {
            api.system.getUsageAnalyticsStatus().then((status) => {
                collectionEnabledRef.current = status.enabled;
                startVisit();
            }).catch(() => {
                collectionEnabledRef.current = true;
                startVisit();
            });
        } else {
            startVisit();
        }

        return () => {
            cancelled = true;
        };
    }, [currentPath, trackingEnabled]);

    useEffect(() => {
        if (!trackingEnabled) return;
        const heartbeat = () => {
            const activeVisit = activeVisitRef.current;
            if (!activeVisit || collectionEnabledRef.current === false || document.visibilityState !== 'visible') return;
            const now = Date.now();
            const recentlyActive = now - lastActivityAtRef.current <= ACTIVE_IDLE_TIMEOUT_MS;
            const elapsedSeconds = Math.max(0, Math.round((now - lastActiveSentAtRef.current) / 1000));
            const activeDelta = recentlyActive ? Math.min(elapsedSeconds, HEARTBEAT_INTERVAL_MS / 1000) : 0;
            if (activeDelta > 0) {
                lastActiveSentAtRef.current = now;
            }
            api.system.updateUsageVisit(activeVisit.visitId, {
                ended: false,
                exit_reason: 'heartbeat',
                active_delta_seconds: activeDelta,
            }).catch(() => undefined);
        };
        const interval = window.setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
        return () => window.clearInterval(interval);
    }, [trackingEnabled]);

    useEffect(() => {
        if (!trackingEnabled) return;

        const endActiveVisit = (exitReason: string, keepalive = false) => {
            const activeVisit = activeVisitRef.current;
            if (!activeVisit) return;
            activeVisitRef.current = null;
            api.system.updateUsageVisit(activeVisit.visitId, { ended: true, exit_reason: exitReason }, { keepalive }).catch(() => undefined);
        };

        const handleVisibilityChange = () => {
            const activeVisit = activeVisitRef.current;
            if (!activeVisit) return;
            if (document.visibilityState === 'hidden') {
                api.system.updateUsageVisit(activeVisit.visitId, { ended: false, exit_reason: 'hidden' }, { keepalive: true }).catch(() => undefined);
            } else {
                api.system.updateUsageVisit(activeVisit.visitId, { ended: false, exit_reason: 'visible' }).catch(() => undefined);
            }
        };

        const handleBeforeUnload = () => {
            endActiveVisit('unload', true);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            endActiveVisit('layout_unmount', true);
        };
    }, [trackingEnabled]);
}
